import React, { useContext, useState, useRef, useEffect } from 'react';
import { Table, Dropdown, Modal, Button } from 'react-bootstrap';
import ReportsService from '../../../../../../../../services/reports-service';
import './corporate-actions-reports.scss';
import { IdentityContext } from '../../../../../../../../context/identity-context';
import ReactDOM from 'react-dom';

const CorporateActionsReports = ({ reports, itemIsin }) => {
  const [expandedReport, setExpandedReport] = useState(null);
  const dropdownRefs = useRef([]);
  const reportService = new ReportsService();
  
  const { user } = useContext(IdentityContext);

  // State for modal and loading
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportToDownload, setReportToDownload] = useState(null);

  const handleDropdownToggle = (id) => {
    setExpandedReport(prev => (prev === id ? null : id));
  };

  const handlePreview = async (report) => {
    try {
      const response = await reportService.retrieveReport(report.type, itemIsin, report.name, report.paymentDate);
      const newWindow = window.open(response.fileURL, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    } catch (error) {
      console.error('Error fetching report preview:', error);
    }
  };

  const handleDownloadConfirmation = (report) => {
    setReportToDownload(report);
    setShowModal(true);
  };

  const handleDownload = async () => {
    setLoading(true);
    
    try {
      const issuerName = user.attributes['custom:display_name'];
      const report = reportToDownload;
      const response = await reportService.retrieveReportIssuer(report.type, itemIsin, report.name, report.paymentDate, issuerName);
      
      const fileResponse = await fetch(response.fileURL);
      if (!fileResponse.ok) throw new Error('Network response was not ok');
      
      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = response.fileName || 'download.pdf';
      link.href = objectUrl;
      link.download = fileName; 
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error fetching report download:', error);
    } finally {
      setShowModal(false);
      setLoading(false);
      window.location.reload(); // Reload the page after download
    }
  };

  // Close the dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside of the dropdown menu
      if (dropdownRefs.current.some(ref => ref && ref.contains(event.target))) {
        return; // Click is inside the dropdown
      }
      // Check if the click is on the dropdown menu (if it exists)
      if (document.getElementById('dropdown-menu') && document.getElementById('dropdown-menu').contains(event.target)) {
        return; // Click is inside the dropdown menu
      }
      setExpandedReport(null); // Close the dropdown
    };

    // Attach event listener if dropdown is expanded
    if (expandedReport !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedReport]);

  return (
    <div className='reportType-table'>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: '50%' }}>Report Name</th>
            <th style={{ width: '20%' }}>Payment Date</th>
            <th style={{ width: '30%' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report, index) => (
            <tr key={report.id}>
              <td>{report.name}</td>
              <td>{report.paymentDate}</td>
              <td className="status-cell">
                {report.status === 'No Reports to Display' ? (
                  report.status
                ) : (
                  <div className="status-container" ref={el => (dropdownRefs.current[index] = el)}>
                    <span className="status-text">{report.status}</span>
                    {report.status === 'Reports Available' && (
                      <Dropdown show={expandedReport === report.id}>
                        <Dropdown.Toggle
                          variant="link"
                          className="dropdown-arrow-button"
                          onClick={() => handleDropdownToggle(report.id)}
                        >
                          ▼
                        </Dropdown.Toggle>
                        {expandedReport === report.id && ReactDOM.createPortal(
                          <div className="dropdown-overlay">
                            <Dropdown.Menu className="dropdown-menu" id="dropdown-menu">
                              <Dropdown.Item onClick={() => {
                                handlePreview(report);
                                setExpandedReport(null);
                              }}>
                                Preview
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => {
                                handleDownloadConfirmation(report);
                                setExpandedReport(null);
                              }}>
                                Download
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </div>,
                          document.body // Render dropdown menu to body
                        )}
                      </Dropdown>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Modal for download confirmation */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Warning: Authorized Access Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            By proceeding to download this report, you confirm that you are an authorized individual with permission to access this sensitive information.
          </p>
          <p>
            Please note that once the report is downloaded, it will no longer be available for viewing through this platform. Ensure that you have the necessary permissions and are ready to download before continuing.
          </p>
          <p>Do you wish to proceed?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleDownload} disabled={loading}>
            {loading ? 'Downloading..' : 'Continue'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CorporateActionsReports;
